import { Bell, Pill, Share2 } from "lucide-react";
import { HEALTH_REMINDERS_ENABLED } from "@shared/constants/features";
import { cn } from "../../lib/cn";
import { StatTile } from "../ui/StatTile";

/**
 * "At a glance" row: compact stat tiles — Shared care links and Alerts, plus
 * Expiring meds only while HEALTH_REMINDERS_ENABLED is on. (The "Children"
 * tile was cut in v2 as redundant with the profile grid right below it.)
 * Purely presentational — the dashboard composes the numbers from data it
 * already loads (notifications) plus the overview query, so each figure is
 * sourced once and can't drift from the list/bell it mirrors.
 *
 * The status tiles (Alerts, Expiring meds) stay calm/neutral at zero and
 * "light up" in their accent hue when there's something to act on.
 *
 * Each tile is a button that asks the DASHBOARD to open a detail modal — this
 * component still owns no data and no dialog state, it only reports the click.
 * Tiles stay clickable at zero (the modal explains the zero); they are disabled
 * only while their source query is still loading.
 */
export type StatTileId = "links" | "alerts" | "medications";
export interface StatRowProps {
  activeLinkCount: number | undefined;
  unreadCount: number | undefined;
  /** Absent while HEALTH_REMINDERS_ENABLED is off (the server doesn't compute it). */
  medicationsExpiringSoon: number | undefined;
  nextMedicationExpiryDays: number | null | undefined;
  /** Feeds the Shared care links + Expiring meds tiles. */
  overviewLoading: boolean;
  /** Feeds the Alerts tile. */
  notificationsLoading: boolean;
  /** Fired when a tile is activated; the dashboard opens the matching modal. */
  onSelect: (tile: StatTileId) => void;
  /** Extra layout classes for the list (the dashboard stacks it beside the activity feed on lg). */
  className?: string;
}

export function StatRow({
  activeLinkCount,
  unreadCount,
  medicationsExpiringSoon,
  nextMedicationExpiryDays,
  overviewLoading,
  notificationsLoading,
  onSelect,
  className,
}: StatRowProps) {
  const alerts = unreadCount ?? 0;
  const expiring = medicationsExpiringSoon ?? 0;
  const links = activeLinkCount ?? 0;

  return (
    // Literal class strings per tile count (Tailwind can't see interpolated
    // names): two tiles share the row evenly at every width; three go 2 + 1 on
    // phones and one row from `sm` up.
    <ul
      className={cn(
        HEALTH_REMINDERS_ENABLED
          ? "grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4"
          : "grid grid-cols-2 gap-3 sm:gap-4",
        className,
      )}
    >
      {/* data-tour / data-shot: the welcome tour's spotlight on the Shared care links tile. */}
      <li className="h-full" data-tour="links-tile" data-shot="links-tile">
        <StatTile
          icon={Share2}
          accent="violet"
          label="Shared care links"
          value={activeLinkCount ?? 0}
          loading={overviewLoading}
          sublabel={overviewLoading ? undefined : links > 0 ? "tap to manage" : "none active"}
          onSelect={() => onSelect("links")}
          actionLabel={`Manage your shared care links (${links} active)`}
        />
      </li>
      <li className="h-full">
        <StatTile
          icon={Bell}
          accent="rose"
          label="Alerts"
          value={unreadCount ?? 0}
          loading={notificationsLoading}
          muted={!notificationsLoading && alerts === 0}
          sublabel={notificationsLoading ? undefined : alerts > 0 ? "needs attention" : "all caught up"}
          onSelect={() => onSelect("alerts")}
          actionLabel={`View your ${alerts} unread ${alerts === 1 ? "alert" : "alerts"}`}
        />
      </li>
      {HEALTH_REMINDERS_ENABLED && (
        <li className="h-full">
          <StatTile
            icon={Pill}
            accent="amber"
            label="Expiring meds"
            value={medicationsExpiringSoon ?? 0}
            loading={overviewLoading}
            muted={!overviewLoading && expiring === 0}
            sublabel={
              overviewLoading
                ? undefined
                : nextMedicationExpiryDays != null
                  ? `in ${nextMedicationExpiryDays}d`
                  : "none soon"
            }
            onSelect={() => onSelect("medications")}
            actionLabel={`View ${expiring} expiring ${expiring === 1 ? "medication" : "medications"}`}
          />
        </li>
      )}
    </ul>
  );
}
